import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

type PageInstructionsProps = {
  title?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  persistKey?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  headerActions?: React.ReactNode;
  icon?: React.ReactNode;
};

const STORAGE_PREFIX = 'epprem_page_instructions:';

export function PageInstructions({
  title = 'What to do on this page',
  children,
  defaultOpen = false,
  persistKey,
  open,
  onOpenChange,
  className,
  headerClassName,
  contentClassName,
  headerActions,
  icon,
}: PageInstructionsProps) {
  const storageKey = useMemo(
    () => (persistKey ? `${STORAGE_PREFIX}${persistKey}` : null),
    [persistKey],
  );
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !storageKey) {
      return defaultOpen;
    }

    const saved = window.localStorage.getItem(storageKey);
    if (saved !== null) {
      return saved === 'true';
    }

    return defaultOpen;
  });

  useEffect(() => {
    if (isControlled || typeof window === 'undefined' || !storageKey) {
      return;
    }

    const saved = window.localStorage.getItem(storageKey);
    if (saved !== null) {
      setInternalOpen(saved === 'true');
    }
  }, [isControlled, storageKey]);

  const resolvedOpen = isControlled ? open : internalOpen;

  useEffect(() => {
    if (typeof window === 'undefined' || !storageKey) {
      return;
    }

    window.localStorage.setItem(storageKey, String(resolvedOpen));
  }, [resolvedOpen, storageKey]);

  const setOpen = (next: boolean) => {
    if (!isControlled) {
      setInternalOpen(next);
    }

    onOpenChange?.(next);
  };

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border bg-muted/30', className)}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={resolvedOpen}
        className={cn(
          'flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5',
          'transition-colors hover:bg-muted/50',
          headerClassName,
        )}
        onClick={() => setOpen(!resolvedOpen)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen(!resolvedOpen);
          }
        }}
      >
        <div className="flex min-w-0 items-center gap-2">
          {icon ?? <Info className="h-3.5 w-3.5 flex-shrink-0 text-primary" />}
          <span className="truncate text-xs font-medium text-foreground">{title}</span>
        </div>

        <div className="flex items-center gap-1">
          {headerActions}
          {resolvedOpen ? (
            <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
      </div>

      <div
        className={cn(
          'overflow-hidden px-3 transition-all duration-200',
          resolvedOpen ? 'max-h-[640px] py-3' : 'max-h-0 py-0',
          contentClassName,
        )}
      >
        <div className="space-y-2 text-xs text-muted-foreground">{children}</div>
      </div>
    </div>
  );
}

export function DefaultInstructions(props: Omit<PageInstructionsProps, 'defaultOpen'>) {
  return <PageInstructions defaultOpen={false} {...props} />;
}

export default PageInstructions;